import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startOfTomorrow } from 'date-fns';
import { Store } from 'components/common/types/Store.types';
import DateRangePicker from 'lib/DatePicker/DateRangePicker';
import { Label } from 'lib/Label';
import { SelectionPermission } from 'store/productManagement/reducer.types';
import { hideSecondaryPanel } from 'store/productManagement/reducer';
import { isValueRequired } from 'components/pages/PCM/Products/utils/isValueRequired';
import { CodeNameModel } from 'components/common/types';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { rangeSpecificPermissionOptions } from '../../consts';
import { isProductScheduleRangeConfirmDisabled } from './utils';
import { ProductScheduleRangeProps } from './ProductScheduleRange.types';
import { secondaryPanelLayoutStyles } from '../../styles';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const ProductScheduleRangePanel: React.FC<ProductScheduleRangeProps> = ({ onConfirm, isReadOnly }) => {
  const dispatch = useDispatch();
  const defaultScheduleRange = useSelector((state: Store) => state.productManagement.newProduct.range);
  const defaultSelectedPermission =
    rangeSpecificPermissionOptions.find(({ code }) => code === defaultScheduleRange?.selectionPermission) ??
    rangeSpecificPermissionOptions[0];

  const [selectedPermission, setSelectedPermission] = useState<CodeNameModel>(defaultSelectedPermission);
  const [from, setFrom] = useState<Date | null>(defaultScheduleRange?.value?.startDate ?? null);
  const [to, setTo] = useState<Date | null>(defaultScheduleRange?.value?.endDate ?? null);

  const hasScheduleRangeChanges =
    selectedPermission.code !== defaultScheduleRange?.selectionPermission ||
    from !== defaultScheduleRange?.value?.startDate ||
    to !== defaultScheduleRange?.value?.endDate;

  return (
    <ProductSecondaryPanel
      title="Schedule"
      onSubmit={() => {
        if (
          (from && to && selectedPermission.code === SelectionPermission.ALLOWED_VALUES) ||
          selectedPermission.code === SelectionPermission.ALL_OPTIONS
        )
          onConfirm({ startDate: from ?? new Date(), endDate: to ?? new Date(), selectedPermission });
      }}
      onCancel={() => dispatch(hideSecondaryPanel())}
      isDisabled={
        isProductScheduleRangeConfirmDisabled({
          selectedPermission,
          startDate: from,
          endDate: to,
          previouslySelectedScheduleRangeValues: {
            startDate: defaultScheduleRange?.value?.startDate,
            endDate: defaultScheduleRange?.value?.endDate,
          },
          previouslySelectedPermission: defaultScheduleRange?.selectionPermission,
        }) || isReadOnly
      }
      customStyles={secondaryPanelLayoutStyles}
      isChange={hasScheduleRangeChanges}
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
        permissionOptions={rangeSpecificPermissionOptions}
      />
      {isValueRequired(selectedPermission.code) ? (
        <>
          <div className="my-4">
            <SectionDivider />
          </div>

          <div className="my-4">
            <Label label="Select range" />
          </div>

          <DateRangePicker
            from={from}
            to={to}
            setFrom={setFrom}
            setTo={setTo}
            width="w-[402px]"
            navigationWidth="w-[378px]"
            isWeekNumbersVisible
            disabledDatePickerDays={[{ before: startOfTomorrow() }]}
          />
        </>
      ) : null}
    </ProductSecondaryPanel>
  );
};

export default ProductScheduleRangePanel;
